import animation from '@helpers/animation';
import { imageUrl } from '@helpers/image';
import { Device, from } from '@helpers/media';
import { sleep } from '@helpers/sleep';
import { withMotion } from '@hoc/withMotion';
import { useInView } from '@hooks/useInView';
import Breadcrumb from '@stories/Components/Breadcrumb/Breadcrumb';
import { Button, LinkButton } from '@stories/Components/Buttons/GeneralButton/Button';
import CarouselControls from '@stories/Components/CarouselControls/CarouselControls';
import CarouselProvider from '@stories/Components/CarouselControls/CarouselProvider';
import SiteWide from '@stories/Components/Global/SiteWide/SiteWide';
import ContactDetails from '@stories/Components/HeroInner/ContactDetails/ContactDetails';
import QuickLinks from '@stories/Components/HeroInner/QuickLinks/QuickLinks';
import Swipeable from '@stories/Components/Swipeable/Swipeable';
import { AnimatePresence } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import ReactPlayer from 'react-player/lazy';
import S from './HeroInner.styles';
export var MediaType;
(function (MediaType) {
    MediaType[MediaType["None"] = 0] = "None";
    MediaType[MediaType["Image"] = 1] = "Image";
    MediaType[MediaType["Video"] = 2] = "Video";
})(MediaType || (MediaType = {}));
const HeroInner = ({ id, breadcrumb, contactDetails, content, cta1, cta2, items, imagePosition = 'above', quickLinks, summary, logo, tag, theme, title, autoplay = 0, loop = false, disableNofollow, }) => {
    const [refContainer, isInView] = useInView({
        rootMargin: animation.inView.earlyRootMargin,
    });
    const [activeIndex, setActiveIndex] = useState(0);
    const [animateSlide, setAnimateSlide] = useState(false);
    const [videoErrored, setVideoErrored] = useState(false);
    const activeItem = items[activeIndex];
    const isExternal = !!activeItem.video &&
        (activeItem.video.indexOf('youtube') > -1 || activeItem.video.indexOf('vimeo') > -1);
    const slideTransitionDelay = 500;
    const cta2Hash = !!cta2?.url && cta2.url.indexOf('#') !== -1;
    useEffect(() => {
        sleep(slideTransitionDelay).then(() => {
            setAnimateSlide(false);
        });
    }, [animateSlide === true]);
    const FallbackImage = () => {
        if (activeItem.fallbackImage === undefined) {
            return null;
        }
        return (React.createElement("picture", null,
            React.createElement("source", { media: from(Device.TabletLarge), sizes: "100vw", srcSet: [
                    `${imageUrl(activeItem.fallbackImage, { width: 1024, heightratio: 750 / 1024 })} 1024w`,
                    `${imageUrl(activeItem.fallbackImage, { width: 1472, heightratio: 750 / 1472 })} 1472w`,
                    `${imageUrl(activeItem.fallbackImage, { width: 1920, heightratio: 750 / 1920 })} 1920w`,
                ].join(',') }),
            React.createElement(S.Image, { alt: "", draggable: false, loading: "eager", role: "presentation", src: "data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==" })));
    };
    function prev() {
        if (loop && activeIndex === 0) {
            return setActiveIndex(items.length - 1);
        }
        setActiveIndex(Math.max(activeIndex - 1, 0));
    }
    function next() {
        if (loop && activeIndex === items.length - 1) {
            return setActiveIndex(0);
        }
        setActiveIndex(Math.min(activeIndex + 1, items.length - 1));
    }
    const renderMedia = () => {
        return (React.createElement(CarouselProvider, { autoplay: activeItem.duration ?? autoplay, loop: loop, max: items.length, value: activeIndex, onChange: (index) => {
                setAnimateSlide(true);
                sleep(slideTransitionDelay).then(() => {
                    setActiveIndex(index);
                });
            } }, ({ actions: { playPause } }) => (React.createElement(Swipeable, { preventDefaultTouchmoveEvent: true, onSwipedLeft: () => {
                setAnimateSlide(true);
                sleep(slideTransitionDelay).then(() => {
                    playPause(true);
                    next();
                });
            }, onSwipedRight: () => {
                setAnimateSlide(true);
                sleep(slideTransitionDelay).then(() => {
                    playPause(true);
                    prev();
                });
            } }, (handlers) => (React.createElement(S.MediaWrapper, { ...handlers },
            React.createElement(AnimatePresence, null,
                activeItem.id && activeItem.mediaType === MediaType.Image && activeItem.image && (React.createElement("picture", null,
                    React.createElement("source", { media: from(Device.TabletLarge), sizes: "100vw", srcSet: [
                            `${imageUrl(activeItem.image, {
                                width: 1024,
                                heightratio: 750 / 1024,
                            })} 1024w`,
                            `${imageUrl(activeItem.image, {
                                width: 1472,
                                heightratio: 750 / 1472,
                            })} 1472w`,
                            `${imageUrl(activeItem.image, {
                                width: 1920,
                                heightratio: 750 / 1920,
                            })} 1920w`,
                        ].join(',') }),
                    React.createElement(S.Image, { key: activeItem.id, initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, alt: "", draggable: false, loading: "eager", role: "presentation", src: "data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==" }))),
                activeItem.id && activeItem.mediaType === MediaType.Video && activeItem.video && (React.createElement(React.Fragment, null, !videoErrored ? (React.createElement(S.Player, { key: activeItem.id, initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, isExternal: isExternal },
                    React.createElement(ReactPlayer, { url: activeItem.video, controls: false, height: "100%", width: "100%", playing: true, playsinline: true, loop: true, muted: true, config: {
                            youtube: {
                                playerVars: { rel: 0 },
                            },
                        }, fallback: React.createElement(FallbackImage, null), onError: () => setVideoErrored(true) }))) : (React.createElement(FallbackImage, null))))),
            items.length > 1 && (React.createElement(S.Controls, { isActive: isInView },
                React.createElement(SiteWide, null,
                    React.createElement(CarouselControls, { controls: true, theme: 'dark' }))))))))));
    };
    return (React.createElement(S.Container, { ref: refContainer, id: id, flush: "both", fullWidth: true, hasImage: activeItem.mediaType !== MediaType.None && imagePosition === 'above', theme: theme, isActive: isInView },
        React.createElement(S.Inner, null,
            imagePosition === 'above' && activeItem.mediaType !== MediaType.None && renderMedia(),
            React.createElement(S.Bottom, null,
                React.createElement(S.BottomInner, null,
                    React.createElement(S.BottomLeft, null,
                        breadcrumb && React.createElement(Breadcrumb, { items: breadcrumb }),
                        tag && (React.createElement(S.Tag, null,
                            React.createElement("span", null, tag))),
                        logo && (React.createElement(S.Logo, { alt: "", loading: "lazy", src: imageUrl(logo, { width: 380 }), invertColour: theme === 'dark' })),
                        title && (React.createElement(S.Title, { isSrOnly: !!logo },
                            React.createElement("span", null, title))),
                        summary && React.createElement(S.Content, { dangerouslySetInnerHTML: { __html: summary } })),
                    React.createElement(S.BottomRight, null,
                        contactDetails && React.createElement(ContactDetails, { ...contactDetails }),
                        quickLinks && React.createElement(QuickLinks, { ...quickLinks }),
                        content && React.createElement(S.Content, { dangerouslySetInnerHTML: { __html: content } }),
                        (cta1?.text || cta2?.text) && (React.createElement(S.CtaWrapper, null,
                            cta1?.text && (React.createElement(LinkButton, { buttonType: theme === 'dark' ? 'orangeExternal' : 'orangeSimple', href: cta1.url, target: cta1.target, title: cta1.text, disableNofollow: disableNofollow })),
                            cta2?.text && (React.createElement(React.Fragment, null, cta2Hash ? (React.createElement(Button, { buttonType: "textOnly", onClick: () => {
                                    if (cta2.url === undefined) {
                                        return;
                                    }
                                    const targetEl = document.querySelector(cta2.url);
                                    targetEl?.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'start',
                                        inline: 'start',
                                    });
                                }, title: cta2.text })) : (React.createElement(LinkButton, { buttonType: "textOnly", href: cta2.url, target: cta2.target, title: cta2.text, disableNofollow: disableNofollow }))))))))),
            imagePosition === 'below' && activeItem.mediaType !== MediaType.None && renderMedia())));
};
export default withMotion(HeroInner);
